import React, { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Star, Square, RefreshCw } from 'lucide-react';
import { useLabels } from '../../config/useLabels';
import { useBrandProfile } from '../../contexts/brand-profile';
import { useGenieChat } from '../../contexts/genie-chat';
import { brandProfileOf } from '../../config/brandProfiles';
import type { LabelKey } from '../../config/labels';
import { MarkdownMessage } from '../MarkdownMessage';
import { StarterChips } from './StarterChips';

/** 1-3 star helpfulness rating shown under each assistant answer. */
function FeedbackStars({
  rating,
  onRate,
  t,
}: {
  rating?: number;
  onRate: (r: number) => void;
  t: (k: LabelKey) => string;
}) {
  const [hover, setHover] = useState(0);
  const rated = typeof rating === 'number';
  return (
    <div className="flex items-center gap-0.5 mt-1 pl-1">
      <span className="text-[11px] text-gray-400 mr-1">{rated ? t('genie.feedbackThanks') : t('genie.feedbackPrompt')}</span>
      {[1, 2, 3].map((n) => {
        const active = (hover || rating || 0) >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onRate(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
            aria-pressed={rating === n}
            className="p-0.5 rounded hover:bg-gray-200/60"
          >
            <Star className={`h-4 w-4 ${active ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
          </button>
        );
      })}
    </div>
  );
}

interface GenieConversationProps {
  /** 'page' lays out inside the /assistant page; 'panel' fills the bubble's chat panel. */
  variant: 'page' | 'panel';
  /** Text to put in the input once, e.g. from a `?q=` deep link. Never sent automatically. */
  prefill?: string;
}

/**
 * The thread, starter questions and input box of the Parent Genie — everything below the
 * heading. The conversation itself lives in GenieChatProvider, so the page and the bubble's
 * panel show the same one.
 */
export function GenieConversation({ variant, prefill }: GenieConversationProps) {
  const t = useLabels();
  const { activeBrand } = useBrandProfile();
  const starterChipKeys = brandProfileOf(activeBrand).assistantStarters;
  const { messages, loading, statusText, lastQuestion, send, stop, rate } = useGenieChat();

  const [input, setInput] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (prefill) setInput(prefill);
  }, [prefill]);

  // Grow the input textarea to fit its content (up to a max), then let it scroll.
  // Runs whenever `input` changes — covers typing, prefill, and reset-after-send.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, variant === 'panel' ? 120 : 200)}px`;
  }, [input, variant]);

  useEffect(() => {
    threadRef.current?.scrollTo?.({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  function ask(question: string) {
    if (!question.trim() || loading) return;
    setInput('');
    void send(question);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    ask(input);
  }

  const isEmpty = messages.length === 0;
  const inPanel = variant === 'panel';

  return (
    <>
      <div
        ref={threadRef}
        className={`flex-1 overflow-y-auto space-y-3 ${inPanel ? 'min-h-0 px-4 py-3' : 'mb-4'}`}
      >
        {isEmpty && <StarterChips keys={starterChipKeys} onPick={ask} disabled={loading} />}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'user' ? (
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white px-4 py-2.5 text-sm whitespace-pre-wrap">
                {m.text}
              </div>
            ) : (
              <div className="flex flex-col items-start max-w-[90%]">
                <div className="rounded-2xl rounded-bl-sm bg-gray-100 text-gray-900 px-4 py-2.5">
                  <MarkdownMessage>{m.text}</MarkdownMessage>
                </div>
                {!m.isError && (
                  <FeedbackStars rating={m.rating} onRate={(r) => rate(i, r)} t={t} />
                )}
                {m.isError && i === messages.length - 1 && lastQuestion && !loading && (
                  <button
                    type="button"
                    onClick={() => ask(lastQuestion)}
                    className="flex items-center gap-1 mt-1 pl-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    <RefreshCw className="h-3 w-3" /> {t('genie.retry')}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-500 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {statusText || t('genie.thinking')}
            </div>
          </div>
        )}
      </div>

      <div className={inPanel ? 'px-4 pt-2 border-t border-gray-100' : ''}>
        {!isEmpty && !loading && <StarterChips keys={starterChipKeys} onPick={ask} compact />}

        <form onSubmit={handleSubmit} className={`flex items-end gap-2 bg-white pt-2 ${inPanel ? '' : 'sticky bottom-0'}`}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ask(input);
              }
            }}
            rows={1}
            placeholder={t('genie.placeholder')}
            disabled={loading}
            className="flex-1 resize-none overflow-y-auto rounded-xl border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
          {loading ? (
            <button
              type="button"
              onClick={stop}
              aria-label={t('genie.stop')}
              title={t('genie.stop')}
              className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              <Square className="h-4 w-4 fill-current" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              aria-label="Send"
              className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
            >
              <Send className="h-5 w-5" />
            </button>
          )}
        </form>
        <p className={`mt-2 text-center text-[11px] leading-snug text-gray-400 ${inPanel ? 'pb-3' : ''}`}>{t('genie.disclaimer')}</p>
      </div>
    </>
  );
}
